import { story } from '@/content/storyBuilder'

/**
 * Akkök Holding — supplied as finished copy on 2026-09-04, with the photography,
 * the two executive portraits and the Akkim / Aktek marks in the same message.
 *
 * The one thing this page does that no other story does: it closes on **two**
 * quotes rather than one, switchable, because the engagement has two principals
 * — Veysi Küçük for the operating company and Fethi Baytan for the technology
 * hub that carries the roadmaps. They are two entries in `content/testimonials/`
 * both pointing at this slug; `StoryBody` draws every quote that names a story,
 * so nothing here configures it.
 */
export const added = story('akkok-holding', 'Akkök Holding', {
  doc: 'c89b287f-7595-4f42-8397-a570906dbd9d-Akkok Holding Digital and AI Maturity Assessment.pdf',
  // The h1 stays the company's name — the mark sits beside it in the header and
  // the page has no eyebrow, so a tagline there would leave the company unnamed
  // on its own story. "What's measured gets done." opens the standfirst instead,
  // which is where the supplied copy puts it in relation to everything under it.
  lede: [
    'Akkök Holding used a shared digital and AI baseline to turn ambition into clear '
    + 'priorities, practical investments and a discipline repeated every year.',
  ],
  intro: [
    '**What’s measured gets done.**',
    'Digital transformation often begins with just a project. At Akkök Holding, it began '
    + 'with **a clear view of where each company stood**.',
  ],
  heroPhoto: {
    src: '/photo/akkok-chemical-plant-at-dusk.webp',
    alt: 'A chemicals plant at dusk, tanks and pipework lit against the sky',
  },
  sections: [
    {
      heading: 'A group, and the companies inside it',
      body: [
        'Akkök Holding is one of Türkiye’s established industrial groups. **Chemicals and '
        + 'advanced materials** sit at its core, alongside energy, real estate and other '
        + 'sectors. In the first phase of the digital and AI maturity assessment, the '
        + 'Holding’s companies operating in chemicals and advanced materials included Aksa, '
        + 'the world’s leading acrylic fiber producer, Akkim in specialty chemicals, Akcoat '
        + 'in coatings and Aksa Carbon in carbon fiber.',
        'Aktek operates as a strategic technology hub that assesses the technology needs of '
        + 'Akkök Holding companies from a centralized perspective, shapes priorities through '
        + 'collective decision-making, and guides their digital transformation journey with '
        + 'robust architectures, secure structures, and long-term technology roadmaps.',
      ],
      photo: {
        src: '/photo/akkok-industrial-site-aerial.webp',
        alt: 'An industrial site from the air, production halls and warehouses at sunrise',
      },
    },
    {
      heading: 'One scale, four companies',
      body: [
        'In the first phase, Digitopia assessed these four companies operating in chemicals '
        + 'and advanced materials using **DAIMI, the Digital and AI Maturity Index**. The '
        + 'companies were measured on the **same evidence-based scale**, compared with one '
        + 'another and benchmarked against Digitopia’s Turkish and global database. Each '
        + 'company then received a **roadmap based on its actual starting point**.',
        'The assessment was designed to answer a practical question: **where should each '
        + 'company focus first?**',
      ],
      photo: {
        src: '/photo/akkok-carbon-fibre-production.webp',
        alt: 'Carbon fibre production line, spools running the length of the hall',
      },
      photoSide: 'right',
      photoInPanel: true,
    },
    {
      heading: 'From one measurement to a management discipline',
      body: [
        'Veysi Küçük, who led the process as CFO of Akkök Holding and now serves as General '
        + 'Manager of Akkim, helped turn the answer into a group-wide management discipline. '
        + 'Guided by the principle that what gets measured gets done, he asked every company '
        + 'to **repeat the assessment each year**. Companies in energy, real estate and other '
        + 'parts of the group are now preparing for their own baselines.',
        'That annual rhythm gave management teams **a common language**. Progress could be '
        + 'tracked over time. Priorities could be discussed with evidence. Budgets, owners '
        + 'and next steps became easier to define.',
      ],
    },
    {
      heading: 'What the findings turned into',
      body: [
        'The findings also shaped a **practical investment agenda**:',
      ],
      items: [
        { text: 'ERP modernization' },
        { text: 'Cloud adoption' },
        { text: 'Cybersecurity' },
        { text: 'Data management' },
        { text: 'Reporting and analytics' },
      ],
      after: [
        '**Strong data and reporting capabilities** give AI initiatives something solid to '
        + 'build on.',
        'The goal is broader than technology. Akkök Holding is working toward more responsive '
        + 'businesses, higher efficiency and productivity, new products and revenue streams, '
        + 'better-informed decisions and **faster execution with less bureaucracy**.',
      ],
    },
    {
      heading: 'Carrying the roadmaps into delivery',
      body: [
        'Led by Akkök CIO and Aktek General Manager, Fethi Baytan, Aktek works with Digitopia '
        + 'to translate the assessment into **coordinated action across the group**. Together, '
        + 'they sequence initiatives, align priorities and keep the roadmaps moving.',
        '**The next assessment is already part of the plan.** That continuity is what turns a '
        + 'snapshot into measurable progress.',
      ],
    },
  ],
  check:
    'The copy on this page was supplied whole and is published as written; what is unchecked '
    + 'is the artwork. The Akkim and Aktek marks were sent as PNGs on a white ground, and the '
    + 'quote card draws a mark straight onto grey — if they print as white rectangles, '
    + 'transparent versions of both are the fix. The LinkedIn post that came with the copy is '
    + 'not on the page, deliberately: it is the same story told for a feed.',
})

/** Requested 2026-09-04: pin as the newest story, first in the grid. */
export const order = -1
